import { GridCell, GridCellKind } from '@glideapps/glide-data-grid';

import type { FinancialSourceAttribution, FinancialSourceNumberCell, FinancialStatementKind } from './types';

interface ResolveFinancialSourceAttributionOptions {
    formattedValue?: string;
    lineItemKey?: string;
    lineItemLabel?: string;
    periodLabel?: string;
    periodKind?: string;
    row: Record<string, unknown>;
    statementKind?: FinancialStatementKind;
    value?: number;
}

interface CreateFinancialSourceNumberCellOptions {
    attribution: FinancialSourceAttribution;
    displayData: string;
    value?: number;
}

export const financialValueCopyData = (value?: number) =>
    value === undefined || !Number.isFinite(value) ? '' : String(value);

const nonEmptyString = (value: unknown) => {
    if (typeof value !== 'string') {
        return undefined;
    }

    const normalized = value.replace(/\s+/g, ' ').trim();

    return normalized || undefined;
};

const safeHttpUrl = (...values: unknown[]) => {
    for (const value of values) {
        const candidate = nonEmptyString(value);

        if (!candidate) {
            continue;
        }

        try {
            const url = new URL(candidate);

            if (url.protocol === 'http:' || url.protocol === 'https:') {
                return candidate;
            }
        } catch {
            // Ignore malformed and non-HTTP source URLs.
        }
    }

    return undefined;
};

const firstString = (...values: unknown[]) => {
    for (const value of values) {
        const candidate = nonEmptyString(value);

        if (candidate) {
            return candidate;
        }
    }

    return undefined;
};

const inferredFilingType = (periodKind?: string, period?: string) => {
    const normalized = `${periodKind ?? ''} ${period ?? ''}`.toLowerCase();

    if (normalized.includes('quarter') || /\bq[1-4]\b/.test(normalized)) {
        return '10-Q';
    }

    if (normalized.includes('annual') || /\bfy\b/.test(normalized) || /\b(19|20)\d{2}\b/.test(normalized)) {
        return '10-K';
    }

    return undefined;
};

export const resolveFinancialSourceAttribution = ({
    formattedValue,
    lineItemKey,
    lineItemLabel,
    periodLabel,
    periodKind,
    row,
    statementKind,
    value,
}: ResolveFinancialSourceAttributionOptions): FinancialSourceAttribution => {
    const period = firstString(
        periodLabel,
        row.periodLabel,
        row.calendarYear,
        row.fiscalYear,
        row.fiscalPeriod,
        row.period,
    );

    return {
        acceptedDate: firstString(row.acceptedDate, row.acceptedAt),
        asReportedLabel: firstString(row.asReportedLabel, row.reportedLabel, row.sourceLineItemLabel, row.sourceLabel),
        cik: firstString(row.cik),
        currency: firstString(row.reportedCurrency, row.currency),
        filingDate: firstString(row.filingDate, row.fillingDate, row.reportDate, row.date),
        filingText: firstString(row.sourceText, row.sourceQuote, row.filingText, row.filingQuote, row.quote),
        filingType: firstString(row.filingType, row.formType, row.form) ?? inferredFilingType(periodKind, period),
        filingUrl: safeHttpUrl(row.finalLink, row.link, row.filingUrl, row.sourceUrl),
        formattedValue,
        lineItemKey,
        lineItemLabel,
        period,
        statementKind,
        symbol: firstString(row.symbol, row.ticker),
        value,
    };
};

export const resolveFinancialStatementKind = (componentKey: string): FinancialStatementKind | undefined => {
    const normalizedKey = componentKey.replace(/[^a-z]/gi, '').toLowerCase();

    if (normalizedKey.includes('income')) {
        return 'income-statement';
    }

    if (normalizedKey.includes('balance')) {
        return 'balance-sheet';
    }

    if (normalizedKey.includes('cashflow')) {
        return 'cash-flow';
    }

    return undefined;
};

export const resolveFinancialFilingPathname = (pathname: string) => {
    const normalizedPathname = pathname.replace(/\/+$/, '');

    if (/^\/search\/(?:companies|funds)\/[^/]+\/[^/]+$/.test(normalizedPathname)) {
        return normalizedPathname.replace(/\/[^/]+$/, '/filings');
    }

    if (/^\/investments\/[^/]+\/analysis\/[^/]+$/.test(normalizedPathname)) {
        return normalizedPathname.replace(/\/[^/]+$/, '/filings');
    }

    return undefined;
};

export const createFinancialFilingHref = (
    filingPathname: string | undefined,
    attribution: FinancialSourceAttribution,
    currentSearch = '',
) => {
    if (!filingPathname || !hasFinancialFilingLocator(attribution)) {
        return undefined;
    }

    const params = new URLSearchParams(currentSearch);

    [
        'acceptedDate',
        'filingDate',
        'filingText',
        'filingType',
        'filingUrl',
        'filingCik',
        'filingSymbol',
        'sourceAsReportedLabel',
        'sourceLabel',
        'sourceLineItem',
        'sourcePeriod',
        'sourceStatement',
        'sourceValue',
    ].forEach((key) => params.delete(key));

    if (attribution.filingUrl) {
        params.set('filingUrl', attribution.filingUrl);
    }

    if (attribution.cik) {
        params.set('filingCik', attribution.cik);
    }

    if (attribution.symbol) {
        params.set('filingSymbol', attribution.symbol);
    }

    if (attribution.filingType) {
        params.set('filingType', attribution.filingType);
    }

    if (attribution.filingDate) {
        params.set('filingDate', attribution.filingDate);
    }

    if (attribution.acceptedDate) {
        params.set('acceptedDate', attribution.acceptedDate);
    }

    if (attribution.filingText) {
        params.set('filingText', attribution.filingText.slice(0, 240));
    }

    if (attribution.statementKind) {
        params.set('sourceStatement', attribution.statementKind);
    }

    if (attribution.asReportedLabel) {
        params.set('sourceAsReportedLabel', attribution.asReportedLabel);
    }

    if (attribution.lineItemKey) {
        params.set('sourceLineItem', attribution.lineItemKey);
    }

    if (attribution.lineItemLabel) {
        params.set('sourceLabel', attribution.lineItemLabel);
    }

    if (attribution.period) {
        params.set('sourcePeriod', attribution.period);
    }

    if (typeof attribution.value === 'number' && Number.isFinite(attribution.value)) {
        params.set('sourceValue', String(attribution.value));
    }

    return `${filingPathname}?${params.toString()}`;
};

export const hasFinancialFilingLocator = ({
    acceptedDate,
    cik,
    filingDate,
    filingType,
    filingUrl,
    symbol,
}: FinancialSourceAttribution) => Boolean(filingUrl || (filingType && (filingDate || acceptedDate || cik || symbol)));

export const createFinancialSourceNumberCell = ({
    attribution,
    displayData,
    value,
}: CreateFinancialSourceNumberCellOptions): FinancialSourceNumberCell => ({
    kind: GridCellKind.Number,
    allowOverlay: true,
    copyData: financialValueCopyData(value),
    data: value,
    displayData,
    readonly: true,
    sourceAttribution: attribution,
});

export const isFinancialSourceNumberCell = (cell: GridCell): cell is FinancialSourceNumberCell =>
    cell.kind === GridCellKind.Number && 'sourceAttribution' in cell && Boolean(cell.sourceAttribution);
